import React, { useEffect } from 'react';
import { Doctor } from '../../types/api';
import { DispensePageProps, TELE_CONSULT_ITEM_CODE, DEFAULT_CONFIRM_MODAL } from '../../types/dispense';
import { apiService } from '../../services/api';
import { useDispenseData } from '../../hooks/useDispenseData';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { normalizeGuid, formatDate, formatDateShort, formatDateSG, convertToWindowsLineBreaks, normalizeLineBreaks, getTodayISO, toDateOnlyISO } from '../../utils';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  AlertModal,
  ConfirmModal,
  MCModal,
  ItemSelectionModal,
  OrderPanelModal,
  EditOrderModal,
  DiagnosisModal,
  DrugAllergyModal,
  DrugAllergyDetailModal,
  MemoTypeModal,
  MemoFormModal
} from '../../components/modals';
import MedicationIcon from '@mui/icons-material/Medication';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { QueueTriageSummary } from '../../components/queue/QueueTriageSummary';
import { parseTriageSummary } from '../../utils/parseTriageSummary';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';
import { HssPanel } from '../../components/hss/HssPanel';
import { ChasBadge } from '../../components/hss/ChasBadge';
import { useOptionalMeetSession } from '../video-consult';
import {
  inferVideoProvider,
  openMeetInNewTab,
  videoConsultProviderLabel,
  withDoctorRoomRole,
  withPortalAccessToken,
  withStaffRoomRole,
} from '../../utils/videoConsultLabel';

const TOTAL_PAGES = 5;
const PAGE_NAMES = ['Case Note History', 'Current Visit', 'Dispense History', 'Memos', 'MC History'];

function DispensePage({ doctor, patient, onBack, onLogout, isExternalConsult = false, portalMode = 'doctor' }: DispensePageProps) {
// ============================================================
  // All state and business logic extracted to custom hook
// ============================================================
  const data = useDispenseData({ doctor, patient, onBack, isExternalConsult, portalMode });
  const meetDock = useOptionalMeetSession();

  // Page navigation using swipe hook
  const { currentPage, setCurrentPage, swipeContainerRef, tabRefs } = useSwipeNavigation({
    initialPage: 1,
    totalPages: TOTAL_PAGES
  });

  // Load history data when page changes
  useEffect(() => {
    data.loadHistoryForPage(currentPage);
  }, [currentPage]);

// ============================================================
  // Derived values
// ============================================================
  const isPatientPicked = data.patientDetails.empId != null && data.patientDetails.empId > 0;
  const canEditCaseNote =
    !data.isCheckedOut &&
    isPatientPicked &&
    !data.isStaffPortal &&
    data.patientDetails.empId === doctor?.id;

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="app-container">
      {/* Mobile menu toggle */}
      {!isExternalConsult && (
        <>
          <button className="mobile-menu-toggle" onClick={() => data.setSidebarOpen(!data.sidebarOpen)}>
            {data.sidebarOpen ? <CloseIcon sx={{ fontSize: 24 }} /> : <MenuIcon sx={{ fontSize: 24 }} />}
          </button>
          <div className={`sidebar-overlay ${data.sidebarOpen ? 'open' : ''}`} onClick={() => data.setSidebarOpen(false)} />
        </>
      )}

      {/* Sidebar */}
      {!isExternalConsult && (
        <aside className={`sidebar ${data.isStaffPortal ? 'staff-tele-sidebar' : ''} ${data.sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-logo sidebar-logo--brand">
            <AspenHealthLogo variant="sidebar" showPortalLabel={data.isStaffPortal} />
          </div>
          <ul className="sidebar-menu">
            <li><a href="#queue" onClick={(e) => { e.preventDefault(); data.handleBack(); }}>{data.isStaffPortal ? 'Staff queue' : 'Patient Queue'}</a></li>
            <li><a href="#dispense" className="active">Dispense</a></li>
          </ul>
          <div className="doctor-info">
            <div className="doctor-name">{doctor?.name}</div>
            <div className="doctor-mcr">MCR: {doctor?.mcr}</div>
            <button className="btn btn-secondary" style={{ marginTop: 12, width: '100%', padding: '8px' }} onClick={onLogout}>Logout</button>
          </div>
        </aside>
      )}

      {/* External Consult Header */}
      {isExternalConsult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>External Consult</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {renderExternalHeaderPickButton()}
            <div style={{ color: '#e0f2fe', fontSize: 13 }}>Dr. {doctor?.name} &bull; MCR: {doctor?.mcr}</div>
          </div>
        </div>
      )}

      <main className="main-content" style={isExternalConsult ? { marginLeft: 0, paddingTop: 60 } : undefined}>
        {!isExternalConsult && (
          <div className="dispense-page-toolbar">
            <button
              type="button"
              className="dispense-back-btn"
              onClick={() => data.handleBack()}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} aria-hidden />
              {data.isStaffPortal ? 'Back to staff queue' : 'Back to patient queue'}
            </button>
          </div>
        )}
        {/* Page Tabs */}
        <div className="page-tabs">
          {PAGE_NAMES.map((page, index) => (
            <button
              key={index}
              ref={(el) => { tabRefs.current[index] = el; }}
              className={`page-tab ${currentPage === index ? 'active' : ''}`}
              onClick={() => setCurrentPage(index)}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Swipeable Container */}
        <div ref={swipeContainerRef} className="swipe-container">
          <div className="swipe-pages" style={{ transform: `translateX(-${currentPage * 100}%)` }}>
            
            {/* ============================== */}
            {/* Page 0: Case Note History      */}
            {/* ============================== */}
            <div className="swipe-page">
              <div className="panel full-height">
                <div className="panel-title">Case Note History</div>
                {data.historyLoading ? (
                  <div className="loading-message">Loading case note history...</div>
                ) : data.caseNoteHistory.length === 0 ? (
                  <div className="empty-message">No case note history found</div>
                ) : (
                  <div className="history-full-list">
                    {data.caseNoteHistory.map(note => {
                      const formattedDate = formatDate(note.dateNote);
                      return (
                        <div key={note.ulid} className="history-card">
                          <div className="history-card-header">
                            <span className="history-date">{formattedDate}</span>
                            <span className="history-store">{note.storeName}</span>
                          </div>
                          <div className="history-doctor">{note.doctorName}</div>
                          <div className="history-diagnosis">{note.diagnosis || 'No diagnosis'}</div>
                          <div className="history-notes">{note.entry}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ============================== */}
            {/* Page 1: Current Visit          */}
            {/* ============================== */}
            <div className="swipe-page">
              <div className="current-visit-content">
                {/* Top Panels: Patient Info and Case Note */}
                <div className="top-panels">
                  {/* Patient Info */}
                  <div className="panel">
                    <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        Patient Information
                        {data.isStaffPortal && data.patientDetails.newPt && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: '#fef2f2',
                              color: '#991b1b',
                              border: '1px solid #fca5a5',
                            }}
                          >
                            New Patient
                          </span>
                        )}
                      </span>
                      {isExternalConsult ? renderExternalPickStatus() : data.isStaffPortal ? renderStaffPickSlot() : renderTeleQueuePickSlot()}
                    </div>
                    <div className="patient-info-grid">
                      <div className="info-item"><label>Name:</label><span>{data.patientDetails.name}</span></div>
                      <div className="info-item"><label>NRIC / Age / Gender:</label><span>{data.patientDetails.nric} • {data.patientDetails.age}yo • {data.patientDetails.gender}</span></div>
                      <div className="info-item"><label>Store:</label><span>{data.patientDetails.store}</span></div>
                      {renderVisitTypeTags()}
                      {renderGoogleMeetLink()}
                      {renderTriageSummary()}
                      {renderAllergyInfo()}
                    </div>
                    {renderVitalsSection()}
                  </div>

                  {/* Case Note */}
                  <div className="panel">
                    <div className="panel-title">
                      Case Note
                      {data.isCheckedOut && <span style={{ marginLeft: 8, fontSize: 12, color: '#22c55e', fontWeight: 'normal' }}>(Checked Out - Read Only)</span>}
                      {!data.isCheckedOut && isPatientPicked && data.isStaffPortal && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b', fontWeight: 'normal' }}>(Read only — assigned doctor edits from doctor queue)</span>
                      )}
                      {!data.isCheckedOut && isPatientPicked && !data.isStaffPortal && data.patientDetails.empId !== doctor?.id && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#dc2626', fontWeight: 'normal' }}>(Picked by another doctor - Read Only)</span>
                      )}
                      {!data.isCheckedOut && !isPatientPicked && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b', fontWeight: 'normal' }}>(Waiting for doctor — Read Only)</span>
                      )}
                    </div>
                    <textarea
                      className="casenote-textarea"
                      placeholder="Enter consultation notes here..."
                      value={data.caseNote}
                      onChange={(e) => {
                        data.setCaseNote(e.target.value);
                        data.setCaseNoteSaved(data.caseNote === data.savedCaseNote && e.target.value === data.savedCaseNote);
                      }}
                      disabled={!canEditCaseNote}
                      style={{
                        opacity: canEditCaseNote ? 1 : 0.6,
                        cursor: canEditCaseNote ? 'text' : 'not-allowed',
                        backgroundColor: canEditCaseNote ? undefined : '#f3f4f6'
                      }}
                    />
                    <div className="casenote-actions">
                      <button
                        className={`btn btn-primary btn-save-casenote ${data.caseNote === data.savedCaseNote ? 'btn-saved' : ''}`}
                        disabled={!canEditCaseNote || data.caseNote === data.savedCaseNote}
                        onClick={handleSaveCaseNote}
                        style={{
                          opacity: canEditCaseNote ? 1 : 0.6,
                          cursor: canEditCaseNote ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {data.caseNoteSaved ? '✓ Saved' : data.caseNote === data.savedCaseNote ? 'Saved' : 'Save Case Note'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Current Orders Summary */}
                {renderOrdersSummary()}

                {/* Create MC Button */}
                {!data.isCheckedOut && (
                  <button
                    className="btn btn-secondary btn-create-mc"
                    onClick={() => {
                      data.setEditingMC(null);
                      data.setMcFromDate(getTodayISO());
                      data.setMcDays(1);
                      data.setMcDateError('');
                      data.setShowMCModal(true);
                    }}
                    disabled={!isPatientPicked}
                    style={{
                      marginBottom: 4,
                      opacity: isPatientPicked ? 1 : 0.6,
                      cursor: isPatientPicked ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <span className="btn-icon">📄</span> Create MC
                  </button>
                )}

                {/* Current Visit MCs */}
                {renderCurrentVisitMCs()}

                {/* HSS (Haze Subsidy Scheme) - mirrors Julius's Haze button */}
                {data.patientDetails?.teleMed && !isExternalConsult && !data.isCheckedOut && (
                  <HssPanel
                    visitULID={patient.visitULID}
                    selection={data.hssSelection}
                    onSelectionChange={data.setHssSelection}
                    readOnly={data.isStaffPortal || Boolean(data.patientDetails?.consultFinalized)}
                    refreshKey={data.patientDetails?.visitStage ?? 0}
                    syncIneligibleApply={!data.showDiagnosisModal}
                  />
                )}

                {/* Save Button */}
                {!data.isCheckedOut && (
                  <button
                    className="btn btn-success btn-save-dispense"
                    onClick={data.handleSaveClick}
                    disabled={!isPatientPicked}
                    style={{
                      opacity: isPatientPicked ? 1 : 0.6,
                      cursor: isPatientPicked ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {isExternalConsult ? 'Save & Close' : 'Save Dispense'}
                  </button>
                )}
                {!data.isCheckedOut && !isExternalConsult && !data.isStaffPortal && data.patientDetails?.teleMed && !data.patientDetails?.walkIn && (
                  <button
                    className="btn btn-primary btn-save-dispense"
                    onClick={data.handleFinishConsult}
                    disabled={!isPatientPicked || data.patientDetails?.consultFinalized}
                    style={{
                      marginTop: 8,
                      opacity: isPatientPicked && !data.patientDetails?.consultFinalized ? 1 : 0.6,
                      cursor: isPatientPicked && !data.patientDetails?.consultFinalized ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {data.patientDetails?.consultFinalized ? 'Consult finished' : 'Finish consult'}
                  </button>
                )}
                {!data.isCheckedOut && !isExternalConsult && !data.isStaffPortal && data.patientDetails?.teleMed && !data.patientDetails?.walkIn && !data.patientDetails?.consultFinalized ? (
                  <p style={{ marginTop: 6, fontSize: 11, color: '#64748b', lineHeight: 1.35 }}>
                    Ends the consult. The patient then chooses pickup or delivery and pays any remaining amount on the portal.
                  </p>
                ) : null}
              </div>
            </div>

            {/* ============================== */}
            {/* Page 2: Dispense History       */}
            {/* ============================== */}
            <div className="swipe-page">
              <div className="panel full-height">
                <div className="panel-title">Dispense History</div>
                {data.historyLoading ? (
                  <div className="loading-message">Loading dispense history...</div>
                ) : data.dispenseHistory.length === 0 ? (
                  <div className="empty-message">No dispense history found</div>
                ) : (
                  <div className="history-full-list">
                    {data.dispenseHistory.map(hist => {
                      const formattedDate = formatDate(hist.visitDate);
                      return (
                        <div key={hist.visitULID} className="history-card">
                          <div className="history-card-header">
                            <span className="history-date">{formattedDate}</span>
                            <span className="history-store">{hist.storeName}</span>
                            <span className="history-total">${hist.totalAmount.toFixed(2)}</span>
                          </div>
                          <div className="dispense-items-list">
                            {hist.items.map((item, i) => {
                              const doseStr = item.dose || '';
                              const freqStr = item.frequency ? ` ${item.frequency}` : '';
                              const doseDisplay = doseStr + freqStr;
                              const itemTotal = item.itemTotal ?? (item as any).ItemTotal ??
                                ((item.unitPrice ?? (item as any).unitPrice ?? 0) * (item.quantity ?? 0));
                              return (
                                <div key={i} className="dispense-history-item">
                                  <span className="item-name">{item.itemName}</span>
                                  <span className="item-qty">x{item.quantity}</span>
                                  {doseDisplay && <span className="item-dose">{doseDisplay}</span>}
                                  <span className="item-price">${(itemTotal ?? 0).toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ============================== */}
            {/* Page 3: Memos                  */}
            {/* ============================== */}
            <div className="swipe-page">
              <div className="panel full-height">
                <div className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Memos</span>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      data.setSelectedMemoType(null);
                      data.setEditingMemo(null);
                      data.setShowMemoTypeModal(true);
                    }}
                    style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 600 }}
                  >
                    + New Memo
                  </button>
                </div>
                {renderMemosList()}
              </div>
            </div>

            {/* ============================== */}
            {/* Page 4: MC History             */}
            {/* ============================== */}
            <div className="swipe-page">
              <div className="panel full-height">
                <div className="panel-title">MC History</div>
                {renderMCHistoryDesktop()}
                {renderMCHistoryMobile()}
              </div>
            </div>
          </div>
        </div>

        {/* Swipe Hint */}
        <div className="swipe-hint mobile-only">← Swipe to navigate →</div>
      </main>

      {/* ============================== */}
      {/* All Modals                     */}
      {/* ============================== */}

      <MCModal
        show={data.showMCModal}
        editingMC={data.editingMC}
        mcFromDate={data.mcFromDate}
        mcDays={data.mcDays}
        mcDateError={data.mcDateError}
        mcRemarks={data.mcRemarks}
        patientName={data.patientDetails.name}
        visitULID={patient.visitULID}
        setShow={data.setShowMCModal}
        setEditingMC={data.setEditingMC}
        setMcFromDate={data.setMcFromDate}
        setMcDays={data.setMcDays}
        setMcDateError={data.setMcDateError}
        setMcRemarks={data.setMcRemarks}
        setAlertModal={data.setAlertModal}
        loadMCHistory={data.loadMCHistory}
      />

      <DrugAllergyModal
        show={data.showDrugAllergyModal}
        drugAllergyDetails={data.drugAllergyDetails}
        drugAllergyLoading={data.drugAllergyLoading}
        drugList={data.drugList}
        drugSearchTerm={data.drugSearchTerm}
        selectedDrug={data.selectedDrug}
        editingDrugAllergy={data.editingDrugAllergy}
        isCheckedOut={data.isCheckedOut}
        isPatientPicked={isPatientPicked}
        patientName={data.patientDetails.name}
        patientNric={data.patientDetails.nric}
        setShow={data.setShowDrugAllergyModal}
        setSelectedDrug={data.setSelectedDrug}
        setDrugSearchTerm={data.setDrugSearchTerm}
        setEditingDrugAllergy={data.setEditingDrugAllergy}
        setIsAddingNewDrugAllergy={data.setIsAddingNewDrugAllergy}
        resetDrugAllergyForm={data.resetDrugAllergyForm}
        handleDrugSearchChange={data.handleDrugSearchChange}
        handleSelectDrug={data.handleSelectDrug}
        handleEditDrugAllergy={data.handleEditDrugAllergy}
        handleRemoveDrugAllergy={data.handleRemoveDrugAllergy}
        handleSaveNoDrugAllergy={data.handleSaveNoDrugAllergy}
      />

      <DrugAllergyDetailModal
        show={data.showDrugAllergyDetailModal}
        selectedDrug={data.selectedDrug}
        isAddingNew={data.isAddingNewDrugAllergy}
        drugAllergyForm={data.drugAllergyForm}
        informationSourceList={data.informationSourceList}
        adverseReactionList={data.adverseReactionList}
        drugAllergyIndicatorList={data.drugAllergyIndicatorList}
        drugProbabilityList={data.drugProbabilityList}
        alertIndicatorList={data.alertIndicatorList}
        reactionOutcomeList={data.reactionOutcomeList}
        setShow={data.setShowDrugAllergyDetailModal}
        setSelectedDrug={data.setSelectedDrug}
        setEditingDrugAllergy={data.setEditingDrugAllergy}
        setIsAddingNewDrugAllergy={data.setIsAddingNewDrugAllergy}
        setDrugAllergyForm={data.setDrugAllergyForm}
        resetDrugAllergyForm={data.resetDrugAllergyForm}
        onSave={data.handleSaveDrugAllergy}
      />

      <MemoTypeModal
        show={data.showMemoTypeModal}
        doctor={doctor}
        patient={patient}
        patientDetails={data.patientDetails}
        setShow={data.setShowMemoTypeModal}
        setSelectedMemoType={data.setSelectedMemoType}
        setMemoForm={data.setMemoForm}
        setShowMemoModal={data.setShowMemoModal}
      />

      <MemoFormModal
        show={data.showMemoModal}
        editingMemo={data.editingMemo}
        selectedMemoType={data.selectedMemoType}
        memoForm={data.memoForm}
        patient={patient}
        setShow={data.setShowMemoModal}
        setEditingMemo={data.setEditingMemo}
        setSelectedMemoType={data.setSelectedMemoType}
        setMemoForm={data.setMemoForm}
        setShowMemoTypeModal={data.setShowMemoTypeModal}
        setAlertModal={data.setAlertModal}
        loadMemos={data.loadMemos}
        loadCurrentOrders={data.loadCurrentOrders}
      />

      <ItemSelectionModal
        show={data.showItemModal}
        showOrderPanel={data.showOrderPanel}
        itemSearch={data.itemSearch}
        filteredItems={data.filteredItems}
        orders={data.orders}
        totalAmount={data.totalAmount}
        patientAllergies={data.patientDetails.allergies}
        itemFilter={data.itemFilter}
        setShow={data.setShowItemModal}
        setItemSearch={data.setItemSearch}
        setItemFilter={data.setItemFilter}
        onSearchChange={data.searchItems}
        onAddItem={data.addItem}
      />

      <OrderPanelModal
        show={data.showOrderPanel}
        orders={data.orders}
        totalAmount={data.totalAmount}
        isPatientPicked={isPatientPicked}
        setShow={data.setShowOrderPanel}
        setShowItemModal={data.setShowItemModal}
        onEditOrder={data.editOrder}
        onRemoveItem={data.removeItem}
        setConfirmModal={data.setConfirmModal}
      />

      <EditOrderModal
        editingOrder={data.editingOrder}
        isAddingNewItem={data.isAddingNewItem}
        setEditingOrder={data.setEditingOrder}
        setIsAddingNewItem={data.setIsAddingNewItem}
        onSave={data.saveEditedOrder}
      />

      <DiagnosisModal
        show={data.showDiagnosisModal}
        selectedAcute={data.selectedAcute}
        selectedChronic={data.selectedChronic}
        diagnosisSearch={data.diagnosisSearch}
        diagnosisTab={data.diagnosisTab}
        expandedCategories={data.expandedCategories}
        patientDetails={data.patientDetails}
        setShow={data.setShowDiagnosisModal}
        setSelectedAcute={data.setSelectedAcute}
        setSelectedChronic={data.setSelectedChronic}
        setDiagnosisSearch={data.setDiagnosisSearch}
        setDiagnosisTab={data.setDiagnosisTab}
        toggleAcute={data.toggleAcute}
        toggleChronic={data.toggleChronic}
        toggleCategory={data.toggleCategory}
        filterDiagnoses={data.filterDiagnoses}
        onConfirmSave={data.handleConfirmSave}
        extraContent={
          data.patientDetails?.teleMed && !isExternalConsult ? (
            <HssPanel
              visitULID={patient.visitULID}
              diagnosisCodes={[...data.selectedAcute, ...data.selectedChronic]}
              selection={data.hssSelection}
              onSelectionChange={data.setHssSelection}
              readOnly={data.isStaffPortal}
              syncIneligibleApply
            />
          ) : null
        }
      />

      <ConfirmModal
        confirmModal={data.confirmModal}
        onDismiss={() => data.setConfirmModal(DEFAULT_CONFIRM_MODAL)}
      />

      <AlertModal
        alertModal={data.alertModal}
        onDismiss={() => data.setAlertModal({ show: false, message: '', type: 'info' })}
      />

      {/* Floating Orders Button (Mobile) */}
      <button className="floating-cart-btn mobile-only" onClick={() => data.setShowOrderPanel(true)}>
        <MedicationIcon sx={{ fontSize: 24 }} />
        {data.orders.length > 0 && <span className="cart-badge">{data.orders.length}</span>}
      </button>
    </div>
  );

  // ============================================================
  // Render Helper Functions
  // ============================================================

  function handleSaveCaseNote() {
    (async () => {
      try {
        const caseNoteForSave = convertToWindowsLineBreaks(data.caseNote);
        const response = await apiService.saveCaseNote(patient.visitULID, caseNoteForSave);
        if (response.success) {
          data.setSavedCaseNote(data.caseNote);
          data.setCaseNoteSaved(true);
          const reloadResponse = await apiService.getCurrentCaseNote(patient.visitULID);
          if (reloadResponse.caseNote) {
            const normalizedCaseNote = normalizeLineBreaks(reloadResponse.caseNote);
            data.setCaseNote(normalizedCaseNote);
            data.setSavedCaseNote(normalizedCaseNote);
          }
          setTimeout(() => data.setCaseNoteSaved(false), 2000);
        } else {
          data.setAlertModal({ show: true, message: response.message || 'Failed to save case note', type: 'error' });
        }
      } catch (error: any) {
        console.error('Error saving case note:', error);
        data.setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not save case note. Please try again.'), type: 'error' });
      }
    })();
  }

  /** Staff portal: doctor pick/unpick is managed on the doctor queue; show assigned doctor only. */
  function renderStaffPickSlot() {
    if (!isPatientPicked) {
      return (
        <span style={{ fontSize: 12, color: '#64748b', maxWidth: 300, textAlign: 'right', lineHeight: 1.4 }}>
          Not yet picked by a doctor — view only until a doctor picks this visit.
        </span>
      );
    }
    const drLabel = data.patientDetails.empCode?.trim() || `Emp ${data.patientDetails.empId}`;
    return (
      <span style={{ fontSize: 12, color: '#0f766e', fontWeight: 600, textAlign: 'right' }}>
        With doctor: {drLabel}
      </span>
    );
  }

  /** CheckLine / WhatsApp consult link: pick lives in the header so queue/staff dispense stay unchanged. */
  function renderExternalPickStatus() {
    const buttonState = data.getPickButtonState();
    if (buttonState.action === 'pick') {
      return (
        <span style={{ fontSize: 12, color: '#64748b', maxWidth: 320, textAlign: 'right', lineHeight: 1.4 }}>
          Not assigned yet — use <strong>Pick up</strong> at the top right to start this consult.
        </span>
      );
    }
    if (buttonState.action === 'unpick') {
      return (
        <span style={{ fontSize: 12, color: '#0f766e', fontWeight: 600, textAlign: 'right' }}>
          You have this visit
        </span>
      );
    }
    return (
      <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600, textAlign: 'right' }}>
        Picked by another doctor
      </span>
    );
  }

  function renderExternalHeaderPickButton() {
    const buttonState = data.getPickButtonState();
    const isPick = buttonState.action === 'pick';
    const isUnpick = buttonState.action === 'unpick';
    const label = isPick ? 'Pick up' : isUnpick ? 'Cancel pick' : (buttonState.text || 'Picked');
    return (
      <button
        type="button"
        className={`btn ${buttonState.enabled ? '' : 'btn-disabled'}`}
        onClick={data.handlePickButtonClick}
        disabled={!buttonState.enabled}
        title={
          buttonState.title ??
          (isPick
            ? 'Assign this visit to yourself and start the consult'
            : isUnpick
              ? 'Release this visit back to the queue'
              : 'This visit is assigned to another doctor')
        }
        style={{
          padding: '6px 14px',
          fontSize: '0.875rem',
          fontWeight: 700,
          cursor: buttonState.enabled ? 'pointer' : 'not-allowed',
          backgroundColor: isPick ? '#fbbf24' : isUnpick ? '#ef4444' : '#94a3b8',
          color: isPick ? '#78350f' : '#ffffff',
          border: 'none',
          borderRadius: 8,
          opacity: buttonState.enabled ? 1 : 0.7,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  }

  /** Main tele portal: pick happens on the queue; dispense only unpicks or shows read-only state. */
  function renderTeleQueuePickSlot() {
    const buttonState = data.getPickButtonState();
    if (buttonState.action === 'pick') {
      return (
        <span style={{ fontSize: 12, color: '#64748b', maxWidth: 300, textAlign: 'right', lineHeight: 1.4 }}>
          Use <strong>Pick</strong> on the Patient Queue to assign this visit to yourself.
        </span>
      );
    }
    return renderPickButton();
  }

  function renderPickButton() {
    const buttonState = data.getPickButtonState();
                        let buttonStyle: React.CSSProperties = {
      padding: '6px 12px', fontSize: '0.875rem',
                          cursor: buttonState.enabled ? 'pointer' : 'not-allowed'
                        };
                        if (buttonState.color === 'red') {
                          buttonStyle.backgroundColor = '#ef4444';
                          buttonStyle.color = '#ffffff';
                          buttonStyle.border = '1px solid #dc2626';
                        } else if (buttonState.color === 'grey') {
                          buttonStyle.backgroundColor = '#e5e7eb';
                          buttonStyle.color = '#6b7280';
                          buttonStyle.border = '1px solid #d1d5db';
                          buttonStyle.opacity = 0.6;
                        } else {
                          buttonStyle.backgroundColor = '#fef3c7';
                          buttonStyle.color = '#92400e';
                          buttonStyle.border = '1px solid #fbbf24';
                        }
                        return (
                          <button
                            className={`btn ${buttonState.enabled ? '' : 'btn-disabled'}`}
        onClick={data.handlePickButtonClick}
                            disabled={!buttonState.enabled}
                            style={buttonStyle}
                            title={buttonState.title ?? (
                              buttonState.action === 'pick' ? 'Pick Patient (F1)' :
                              buttonState.action === 'unpick' ? 'Cancel Pick (Unpick Patient)' :
                              'Patient picked by another user'
                            )}
                          >
                            {buttonState.text}
                          </button>
                        );
  }

  function renderGoogleMeetLink() {
    if (!data.patientDetails.teleMed) return null;
    const providerLabel = videoConsultProviderLabel(
      data.patientDetails.corporateCompanyName,
      data.teleConsultMeetUrl,
    );
    const roleUrl = data.teleConsultMeetUrl
      ? data.isStaffPortal
        ? withStaffRoomRole(data.teleConsultMeetUrl)
        : withDoctorRoomRole(data.teleConsultMeetUrl)
      : undefined;
    const joinUrl = roleUrl ? withPortalAccessToken(roleUrl) : undefined;
    const isJitsi = inferVideoProvider(data.teleConsultMeetUrl) === 'Jitsi';
    const thisVisitInDock = Boolean(meetDock?.enabled && data.teleConsultMeetUrl && meetDock.isThisVisit(patient.visitULID));
    const openInDock = () => {
      if (!roleUrl || !meetDock?.enabled)
        return;
      meetDock.startMeet({
        visitUlid: patient.visitULID,
        role: data.isStaffPortal ? 'staff' : 'doctor',
        joinUrl: roleUrl,
      });
    };
    return (
      <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: 4 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>{providerLabel} (this visit)</label>
        {data.teleConsultMeetLoading ? (
          <span style={{ color: '#64748b', fontSize: 14 }}>Preparing {providerLabel} link…</span>
        ) : data.teleConsultMeetSkipped ? (
          <span style={{ color: '#64748b', fontSize: 14 }}>{providerLabel} link is not enabled on the server for this environment.</span>
        ) : data.teleConsultMeetUrl ? (
          <>
            <div className="dispense-meet-btn-row">
            {thisVisitInDock ? (
              <>
                <button
                  type="button"
                  className="btn dispense-meet-btn"
                  onClick={() => meetDock?.setMode(meetDock.mode === 'full' ? 'mini' : 'full')}
                >
                  {meetDock?.mode === 'full' ? 'Minimize video' : 'Expand video'}
                </button>
                <button
                  type="button"
                  className="btn dispense-meet-btn dispense-meet-btn--leave"
                  onClick={() => meetDock?.endMeet()}
                >
                  Leave
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn dispense-meet-btn"
                onClick={() => {
                  if (isJitsi && meetDock?.enabled)
                    openInDock();
                  else if (joinUrl)
                    openMeetInNewTab(joinUrl);
                }}
                title={joinUrl}
              >
                <svg className="dispense-meet-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Join Video Consultation ({data.isStaffPortal ? 'staff' : providerLabel})
              </button>
            )}
            {isJitsi && joinUrl ? (
              <button
                type="button"
                className="btn dispense-meet-btn dispense-meet-btn--tab"
                onClick={() => openMeetInNewTab(joinUrl)}
              >
                Open in new tab
              </button>
            ) : null}
            </div>
            <p className="dispense-meet-btn__hint">
              {isJitsi
                ? 'Video stays on this page. Minimize to write notes. Leave does not close the room — it stays open until Julius checkout.'
                : data.isStaffPortal
                  ? 'Joins as you (staff name, moderator). Leave does not close the room — it stays open until Julius checkout.'
                  : 'Opens your consultation link in a new tab. Leave does not close the room — it stays open until Julius checkout.'}
            </p>
            {(() => {
              const s = data.meetLiveStatus;
              if (!s || s.source === 'unavailable') {
                return null;
              }
              if (s.source === 'jitsi') {
                const micOff = Boolean(s.patientInRoom && s.patientAudioMuted);
                const micOn = Boolean(s.patientInRoom && s.patientAudioMuted === false);
                return (
                  <div className="dispense-meet-live">
                    <div
                      className={`dispense-meet-live__room${s.patientInRoom ? ' is-in' : ''}`}
                    >
                      {s.patientInRoom ? 'Patient is in Aspen Meet' : 'Patient is not in the meeting room'}
                      {s.patientBrowser ? ` · ${s.patientBrowser}` : ''}
                      {s.staffInRoom ? ' · staff is in the room' : ''}
                      {s.doctorInRoom ? ' · doctor is in the room' : ''}
                    </div>
                    {micOff ? (
                      <div className="dispense-meet-live__mic is-off" role="status">
                        Patient microphone is OFF — you cannot hear them
                      </div>
                    ) : null}
                    {micOn ? (
                      <div className="dispense-meet-live__mic is-on" role="status">
                        Patient microphone is ON
                      </div>
                    ) : null}
                  </div>
                );
              }
              if (s.hasActiveConference) {
                if (s.activeParticipantCount != null)
                  return (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        background: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #86efac',
                      }}
                    >
                      Live · {s.activeParticipantCount} in call
                    </div>
                  );
                return (
                  <div
                    style={{
                      marginTop: 8,
                      display: 'inline-flex',
                      padding: '4px 10px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      background: '#fef9c3',
                      color: '#854d0e',
                      border: '1px solid #fde047',
                    }}
                  >
                    Call active
                  </div>
                );
              }
              return (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                  No active Meet session right now (room empty or not started).
                </div>
              );
            })()}
          </>
        ) : (
          <span style={{ color: '#64748b', fontSize: 14 }}>No Meet link returned for this visit.</span>
        )}
      </div>
    );
  }

  function renderVisitTypeTags() {
    const { teleMed, corporateCompanyName, newPt, walkIn } = data.patientDetails;
    if (!teleMed && !corporateCompanyName && !newPt) return null;
    return (
                        <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {newPt && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}>
            New Patient
          </span>
        )}
        {teleMed && walkIn && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' }}>
                              📍 Onsite Tele
                            </span>
                          )}
        {teleMed && !walkIn && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
                              🌐 Offsite Tele
                            </span>
                          )}
        {corporateCompanyName && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd' }}>
            🏢 {corporateCompanyName}
                            </span>
                          )}
        {data.patientDetails.chas ? <ChasBadge chas={data.patientDetails.chas} showText /> : null}
                        </div>
    );
  }

  function renderTriageSummary() {
    const lines = parseTriageSummary(data.patientDetails.triageSummary);
    if (lines.length === 0) return null;
    return (
      <div className="info-item dispense-triage-summary">
        <QueueTriageSummary
          summary={data.patientDetails.triageSummary}
          isLightBg
          label="AI triage summary"
        />
      </div>
    );
  }

  function renderAllergyInfo() {
    return (
                      <div className="info-item allergy-info" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {data.patientDetails.allergies && data.patientDetails.allergies.length > 0 ? (
                            <>
                              <label>Allergies:</label>
              <span className="allergy-badge">{data.patientDetails.allergies.join(', ')}</span>
                            </>
                          ) : (
                            <label style={{ color: '#64748b' }}>No known drug allergies</label>
                          )}
                        </div>
        {!data.isCheckedOut && isPatientPicked && (
                          <button
                            className={`btn btn-secondary ${
              data.drugAllergyValidation === null ? '' :
              !data.drugAllergyValidation.hasDrugAllergyRecord ? 'drug-allergy-btn-unconfirmed' :
              data.patientDetails.allergies && data.patientDetails.allergies.length > 0 ? 'drug-allergy-btn-has-allergy' :
                              'drug-allergy-btn-confirmed'
                            }`}
            onClick={data.handleOpenDrugAllergyModal}
            style={{ padding: '4px 12px', fontSize: '12px', minWidth: 'auto' }}
                            title="Manage Drug Allergies"
                          >
            {data.drugAllergyValidation === null ? '💊 Drug Allergy' :
             !data.drugAllergyValidation.hasDrugAllergyRecord ? '⚠️ Unconfirmed' :
             data.patientDetails.allergies && data.patientDetails.allergies.length > 0 ? '⚠️ Has Allergy' :
                             '✅ NKDA'}
                          </button>
                        )}
                      </div>
    );
  }
                    
  function renderVitalsSection() {
    const pd = data.patientDetails;
    return (
                    <div className="vitals-section">
                      <div className="vitals-compact">
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">Temp:</span>
            <span className={`vital-value-compact ${pd.temp >= 38 ? 'abnormal' : ''}`}>
              {pd.temp > 0 ? `${pd.temp.toFixed(1)}°C` : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">BP:</span>
            <span className={`vital-value-compact ${pd.sbp >= 140 ? 'abnormal' : ''}`}>
              {pd.sbp > 0 && pd.dbp > 0 ? `${pd.sbp}/${pd.dbp}` : '--/--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">Pulse:</span>
            <span className={`vital-value-compact ${pd.pulse < 60 || pd.pulse > 100 ? 'abnormal' : ''}`}>
              {pd.pulse > 0 ? `${pd.pulse}` : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">SpO2:</span>
            <span className={`vital-value-compact ${pd.spo2 < 95 ? 'abnormal' : ''}`}>
              {pd.spo2 > 0 ? `${pd.spo2}%` : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">Wt:</span>
                          <span className="vital-value-compact">
              {pd.weight && pd.weight > 0 ? `${pd.weight.toFixed(1)}kg` : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">Ht:</span>
                          <span className="vital-value-compact">
              {pd.height && pd.height > 0 ? `${pd.height.toFixed(0)}cm` : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">BMI:</span>
                          <span className="vital-value-compact">
              {pd.weight && pd.height && pd.height > 0
                ? (pd.weight / ((pd.height / 100) * (pd.height / 100))).toFixed(1)
                              : '--'}
                          </span>
                        </div>
                        <div className="vital-compact-item">
                          <span className="vital-label-compact">Waist:</span>
                          <span className="vital-value-compact">
              {pd.waist && pd.waist > 0 ? `${pd.waist.toFixed(1)}cm` : '--'}
                          </span>
                        </div>
          {pd.remarks && (
                          <div className="vital-compact-item vital-remarks-compact">
                            <span className="vital-label-compact">Remarks:</span>
              <span className="vital-value-compact">{pd.remarks}</span>
                          </div>
                        )}
                      </div>
                    </div>
    );
  }

  function renderOrdersSummary() {
    return (
                <div className="panel orders-summary">
                  <div className="orders-summary-header">
                    <span className="panel-title" style={{ margin: 0 }}>
            Current Orders ({data.orders.length})
            {data.isCheckedOut && <span style={{ marginLeft: 8, fontSize: 12, color: '#22c55e', fontWeight: 'normal' }}>(Checked Out - Read Only)</span>}
                    </span>
          {data.orders.length > 0 && <span className="orders-total">${data.totalAmount.toFixed(2)}</span>}
                  </div>
        {data.ordersLoading ? (
                    <div className="loading-message">Loading orders...</div>
        ) : data.orders.length > 0 ? (
                    <div className="orders-list">
            {data.orders.map(order => {
                        const isTeleConsult = order.code === TELE_CONSULT_ITEM_CODE;
              const canEdit = !data.isCheckedOut && !isTeleConsult && isPatientPicked;
                        return (
                        <div key={order.id} className="order-list-item">
                          <div 
                            className="order-list-item-content" 
                    onClick={() => { if (canEdit) data.editOrder(order); }}
                            style={{
                              cursor: canEdit ? 'pointer' : isTeleConsult ? 'default' : 'not-allowed',
                              opacity: canEdit || isTeleConsult ? 1 : 0.6
                            }}
                          >
                            <div className="order-list-item-info">
                              <div className="order-list-item-name">
                                {order.publishName || order.name}
                                {isTeleConsult && <span style={{ marginLeft: 6, fontSize: 10, color: '#64748b', fontWeight: 'normal' }}>(Auto)</span>}
                              </div>
                              <div className="order-list-item-details">
                                {order.type === 'MED' && order.dosage && order.frequency && (
                                  <span className="order-detail">{order.dosage} {order.doseUOM || 'tablet'} {order.frequency} • </span>
                                )}
                                <span className="order-detail">Qty: {order.quantity || order.qty || 1} {order.dispenseUOM || 'unit'}</span>
                              </div>
                            </div>
                            <div className="order-list-item-price">${((order.unitPrice || order.price) * (order.quantity || order.qty || 1)).toFixed(2)}</div>
                          </div>
                  {!data.isCheckedOut && !isTeleConsult && (
                            <button 
                              className="order-list-item-remove" 
                              onClick={(e) => { 
                                e.stopPropagation();
                        if (!isPatientPicked) return;
                        data.setConfirmModal({
                                  show: true,
                                  message: `Remove ${order.publishName || order.name} from orders?`,
                          onConfirm: () => { data.removeItem(order.id); data.setConfirmModal(DEFAULT_CONFIRM_MODAL); },
                          onCancel: () => { data.setConfirmModal(DEFAULT_CONFIRM_MODAL); }
                                });
                              }}
                      disabled={!isPatientPicked}
                              title="Remove item"
                              style={{
                        opacity: isPatientPicked ? 1 : 0.6,
                        cursor: isPatientPicked ? 'pointer' : 'not-allowed'
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="orders-empty">No items added yet</div>
                  )}
        {!data.isCheckedOut && (
                      <button 
                        className="btn btn-primary btn-add-item" 
            onClick={() => data.setShowItemModal(true)}
            disabled={!isPatientPicked}
                        style={{ 
              marginTop: 12, width: '100%',
              opacity: isPatientPicked ? 1 : 0.6,
              cursor: isPatientPicked ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <span className="btn-icon">+</span> Add Item
                      </button>
                  )}
                </div>
    );
  }

  function renderCurrentVisitMCs() {
    const currentVisitMCs = data.mcHistory.filter(mc => {
                  const mcVisitULIDRaw = (mc as any).visitULID || (mc as any).VisitULID || (mc as any).visitUlid || (mc as any).VisitUlid;
                  return normalizeGuid(mcVisitULIDRaw) === normalizeGuid(patient.visitULID) && normalizeGuid(mcVisitULIDRaw) !== '';
    });
    if (currentVisitMCs.length === 0) return null;

    return (
                  <div style={{ marginBottom: 8 }}>
        {currentVisitMCs.map(mc => (
                      <div key={mc.ulid} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', marginBottom: 4, background: '#f0fdf4',
            border: '1px solid #86efac', borderRadius: 8, fontSize: 13
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                          <span>📄</span>
                          <span style={{ fontWeight: 600, color: '#166534' }}>
                            {formatDateShort(mc.dateStart)} – {formatDateShort(mc.dateEnd)}
                          </span>
                          <span style={{ color: '#15803d' }}>({mc.days} day{mc.days !== 1 ? 's' : ''})</span>
                        </div>
            {!data.isCheckedOut && (
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => {
                    data.setEditingMC(mc);
                    data.setMcFromDate(toDateOnlyISO(mc.dateStart));
                    data.setMcDays(mc.days);
                    data.setMcDateError('');
                    data.setShowMCModal(true);
                              }}
                              style={{ fontSize: 11, padding: '3px 8px', minWidth: 'auto' }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => {
                    data.setConfirmModal({
                                  show: true,
                                  message: 'Are you sure you want to delete this MC?',
                                  onConfirm: async () => {
                                    try {
                                      const result = await apiService.deleteMC(patient.visitULID, mc.ulid);
                                      if (result.success) {
                            data.setAlertModal({ show: true, message: 'MC deleted successfully!', type: 'success' });
                            await data.loadMCHistory();
                                      } else {
                            data.setAlertModal({ show: true, message: result.message || 'Failed to delete MC.', type: 'error' });
                                      }
                                    } catch (error: any) {
                          data.setAlertModal({ show: true, message: getApiErrorMessage(error, 'Something went wrong. Please try again.'), type: 'error' });
                                    }
                        data.setConfirmModal(DEFAULT_CONFIRM_MODAL);
                                  },
                      onCancel: () => data.setConfirmModal(DEFAULT_CONFIRM_MODAL)
                                });
                              }}
                              style={{ fontSize: 11, padding: '3px 8px', minWidth: 'auto' }}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                                </div>
                              );
  }

  function renderMemosList() {
    if (data.memoLoading) {
      return <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading memos...</div>;
    }
    if (data.memos.length === 0) {
      return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>No memos found</div>
                    <div style={{ fontSize: '14px' }}>Click "New Memo" to create one</div>
                  </div>
      );
    }
    return (
                  <div style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {data.memos.map((memo) => (
                        <div
                          key={memo.ulid}
                          style={{
                background: '#ffffff', border: '2px solid #e5e7eb', borderRadius: '12px',
                padding: '20px', cursor: 'pointer', transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#0284c7';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(2, 132, 199, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
              onClick={() => { data.setEditingMemo(memo); data.setShowMemoModal(true); }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                                {memo.memoTypeName || `Memo Type ${memo.memoType}`}
                              </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    {memo.memoDate ? formatDateSG(memo.memoDate) : 'No date'}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                    data.setConfirmModal({
                                  show: true,
                                  message: 'Are you sure you want to delete this memo?',
                                  onConfirm: async () => {
                                    try {
                                      const response = await apiService.deleteMemo(patient.visitULID, memo.ulid);
                                      if (response.success) {
                            data.setAlertModal({ show: true, message: 'Memo deleted successfully', type: 'success' });
                            await data.loadMemos();
                            await data.loadCurrentOrders();
                                      } else {
                            data.setAlertModal({ show: true, message: response.message || 'Failed to delete memo', type: 'error' });
                                      }
                                    } catch (error: any) {
                          data.setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not delete memo. Please try again.'), type: 'error' });
                        }
                        data.setConfirmModal(DEFAULT_CONFIRM_MODAL);
                      },
                      onCancel: () => { data.setConfirmModal(DEFAULT_CONFIRM_MODAL); }
                                });
                              }}
                              style={{
                    background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '6px',
                    padding: '4px 8px', fontSize: '12px', color: '#dc2626', cursor: 'pointer', fontWeight: 600
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                            >
                              Delete
                            </button>
                          </div>
                          {memo.recipient && (
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>To:</div>
                              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500 }}>{memo.recipient}</div>
                            </div>
                          )}
                          {memo.subHeader && (
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>Re:</div>
                              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500 }}>{memo.subHeader}</div>
                            </div>
                          )}
                          {memo.body && (
                            <div style={{
                  fontSize: '13px', color: '#475569', lineHeight: '1.5', maxHeight: '60px',
                  overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                  WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'
                            }}>
                              {memo.body}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
    );
  }

  function renderMCHistoryDesktop() {
    return (
                <div className="mc-history-table-container desktop-only">
                  <table className="mc-history-table">
                    <thead>
                      <tr>
              <th>Date</th><th>Store</th><th>Doctor</th><th>MC Period</th><th>Days</th><th>Diagnosis</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
            {data.historyLoading ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
            ) : data.mcHistory.length === 0 ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>No MC history found</td></tr>
                      ) : (
              data.mcHistory.map(mc => {
                          const mcVisitULIDRaw = (mc as any).visitULID || (mc as any).VisitULID || (mc as any).visitUlid || (mc as any).VisitUlid;
                const isCurrentVisitMC = normalizeGuid(mcVisitULIDRaw) === normalizeGuid(patient.visitULID) && normalizeGuid(mcVisitULIDRaw) !== '';
                          return (
                            <tr key={mc.ulid}>
                              <td>{formatDateShort(mc.certificateDate)}</td>
                              <td>{mc.storeName}</td>
                              <td>{mc.doctorName}</td>
                              <td>{formatDateShort(mc.dateStart)} - {formatDateShort(mc.dateEnd)}</td>
                              <td>{mc.days}</td>
                              <td>{mc.excuseDetail || mc.remarks || '-'}</td>
                              <td>
                      {isCurrentVisitMC && !data.isCheckedOut ? (
                                  <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-small btn-secondary" onClick={() => {
                            data.setEditingMC(mc);
                            data.setMcFromDate(toDateOnlyISO(mc.dateStart));
                            data.setMcDays(mc.days);
                            data.setMcDateError('');
                            data.setShowMCModal(true);
                          }} style={{ fontSize: '12px', padding: '4px 8px' }}>Edit</button>
                          <button className="btn btn-small btn-danger" onClick={() => {
                            data.setConfirmModal({
                                          show: true,
                                          message: 'Are you sure you want to delete this MC? This action cannot be undone.',
                                          onConfirm: async () => {
                                            try {
                                              const result = await apiService.deleteMC(patient.visitULID, mc.ulid);
                                              if (result.success) {
                                    data.setAlertModal({ show: true, message: 'Medical Certificate deleted successfully!', type: 'success' });
                                    await data.loadMCHistory();
                                              } else {
                                    data.setAlertModal({ show: true, message: result.message || 'Failed to delete MC.', type: 'error' });
                                              }
                                            } catch (error: any) {
                                              console.error('Error deleting MC:', error);
                                  data.setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not delete medical certificate. Please try again.'), type: 'error' });
                                }
                                data.setConfirmModal(DEFAULT_CONFIRM_MODAL);
                              },
                              onCancel: () => { data.setConfirmModal(DEFAULT_CONFIRM_MODAL); }
                            });
                          }} style={{ fontSize: '12px', padding: '4px 8px' }}>Remove</button>
                                  </div>
                                ) : (
                                  <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
    );
  }

  function renderMCHistoryMobile() {
    return (
                <div className="mc-history-cards mobile-only">
        {data.historyLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
        ) : data.mcHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>No MC history found</div>
                  ) : (
          data.mcHistory.map(mc => {
                      const mcVisitULIDRaw = (mc as any).visitULID || (mc as any).VisitULID || (mc as any).visitUlid || (mc as any).VisitUlid;
            const isCurrentVisitMC = normalizeGuid(mcVisitULIDRaw) === normalizeGuid(patient.visitULID) && normalizeGuid(mcVisitULIDRaw) !== '';
                      return (
                        <div key={mc.ulid} className="mc-history-card">
                          <div className="mc-card-header">
                            <div className="mc-card-date">{formatDateShort(mc.certificateDate)}</div>
                            <div className="mc-card-days">{mc.days} {mc.days === 1 ? 'Day' : 'Days'}</div>
                          </div>
                          <div className="mc-card-body">
                  <div className="mc-card-row"><span className="mc-card-label">Store:</span><span className="mc-card-value">{mc.storeName}</span></div>
                  <div className="mc-card-row"><span className="mc-card-label">Doctor:</span><span className="mc-card-value">{mc.doctorName}</span></div>
                  <div className="mc-card-row"><span className="mc-card-label">MC Period:</span><span className="mc-card-value">{formatDateShort(mc.dateStart)} - {formatDateShort(mc.dateEnd)}</span></div>
                  <div className="mc-card-row mc-card-diagnosis"><span className="mc-card-label">Diagnosis:</span><span className="mc-card-value">{mc.excuseDetail || mc.remarks || '-'}</span></div>
                  {isCurrentVisitMC && !data.isCheckedOut && (
                            <div className="mc-card-row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px' }}>
                      <button className="btn btn-small btn-secondary" onClick={() => {
                        data.setEditingMC(mc);
                        data.setMcFromDate(toDateOnlyISO(mc.dateStart));
                        data.setMcDays(mc.days);
                        data.setMcRemarks(mc.remarks || '');
                        data.setMcDateError('');
                        data.setShowMCModal(true);
                      }} style={{ flex: 1, fontSize: '12px', padding: '6px 12px' }}>Edit</button>
                      <button className="btn btn-small btn-danger" onClick={() => {
                        data.setConfirmModal({
                                    show: true,
                                    message: 'Are you sure you want to delete this MC? This action cannot be undone.',
                                    onConfirm: async () => {
                                      try {
                                        const result = await apiService.deleteMC(patient.visitULID, mc.ulid);
                                        if (result.success) {
                                data.setAlertModal({ show: true, message: 'Medical Certificate deleted successfully!', type: 'success' });
                                await data.loadMCHistory();
                                        } else {
                                data.setAlertModal({ show: true, message: result.message || 'Failed to delete MC.', type: 'error' });
                                        }
                                      } catch (error: any) {
                                        console.error('Error deleting MC:', error);
                              data.setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not delete medical certificate. Please try again.'), type: 'error' });
                            }
                            data.setConfirmModal(DEFAULT_CONFIRM_MODAL);
                          },
                          onCancel: () => { data.setConfirmModal(DEFAULT_CONFIRM_MODAL); }
                        });
                      }} style={{ flex: 1, fontSize: '12px', padding: '6px 12px' }}>Remove</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                    )}
                  </div>
                );
  }
}

export default DispensePage;
